import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import CenteredModal from '../ui/CenteredModal';
import AssignmentDetails from '../assignments/AssignmentDetails';
import EventDetails from '../events/EventDetails';
import AddToCalendarModal from '../calendar/AddToCalendarModal/AddToCalendarModal';
import useIsMobile from '../../hooks/useIsMobile';
import NextActionCard from './NextActionCard';
import EventGroup from './EventGroup';
import CompletedSection from './CompletedSection';
import ProgressFooter from './ProgressFooter';
import ScoutSuggestion from './ScoutSuggestion';
import Coachmark from './Coachmark';
import './UnifiedActivities.css';

/* ─────────────────────────────────────────────
   Helpers
   ───────────────────────────────────────────── */

/** Parse any date string the backend might return */
const parseDate = (raw) => {
  if (!raw) return null;
  const d = typeof raw === 'string'
    ? (raw.includes('T') ? new Date(raw) : new Date(`${raw}T00:00:00`))
    : new Date(raw);
  return isNaN(d.getTime()) ? null : d;
};

/** Human-readable relative date */
const formatRelativeDate = (dateStr) => {
  const d = parseDate(dateStr);
  if (!d) return 'No date';
  const now = new Date();
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);

  const timeOpts = { hour: 'numeric', minute: '2-digit', hour12: true };

  if (d.toDateString() === now.toDateString()) {
    return `Today at ${d.toLocaleTimeString('en-US', timeOpts)}`;
  }
  if (d.toDateString() === tomorrow.toDateString()) {
    return `Tomorrow at ${d.toLocaleTimeString('en-US', timeOpts)}`;
  }
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

/** Is a date within the next N days? */
const isWithinDays = (dateStr, days) => {
  const d = parseDate(dateStr);
  if (!d) return false;
  const now = new Date();
  const limit = new Date(now.getTime() + days * 86400000);
  return d >= now && d <= limit;
};

/** Is a date in the past? */
const isPast = (dateStr) => {
  const d = parseDate(dateStr);
  return d ? d < new Date() : false;
};

/* Filter presets */
const FILTER_OPTIONS = [
  { value: 'all',       label: 'All' },
  { value: 'nextClass', label: 'Next Class' },
  { value: 'next14',    label: 'Next 14 Days' },
  { value: 'overdue',   label: 'Overdue' },
];

/**
 * Compute per-session "next class" cutoff.
 * For each active session group, the cutoff is the session's own date.
 * A task is included if it's due before (or on) that session date,
 * OR if it's explicitly parented to that session.
 * This filter surfaces the slice of work relevant to "before my next meeting."
 */
function computeNextClassGroups(groups) {
  const now = new Date();

  // Find the earliest future session — that is "next class"
  const futureGroups = groups
    .filter(g => {
      const d = parseDate(g.date);
      return d && d >= now;
    })
    .sort((a, b) => (parseDate(a.date)?.getTime() ?? 0) - (parseDate(b.date)?.getTime() ?? 0));

  if (futureGroups.length === 0) return [];

  const nextSession = futureGroups[0];
  const cutoff = parseDate(nextSession.date)?.getTime() ?? 0;

  // Include this "next class" session plus any earlier sessions that still have incomplete tasks
  return groups
    .map(g => {
      const gTime = parseDate(g.date)?.getTime() ?? 0;
      // The next-class session itself: show all its tasks
      if (g.id === nextSession.id) return g;
      // Earlier sessions: include tasks due before the cutoff (including overdue)
      if (gTime <= cutoff) {
        const filtered = g.assignments.filter(a => {
          const aTime = parseDate(a.date)?.getTime() ?? 0;
          return aTime <= cutoff;
        });
        if (filtered.length > 0) return { ...g, assignments: filtered };
      }
      return null;
    })
    .filter(Boolean);
}

/** Normalize event shape for EventDetails modal */
const normalizeEventForModal = (event) => {
  if (!event) return null;
  const start = event.start || event.startDatetime || event.startDate
    || event.start_date || event.eventDate || event.dueDate;
  const end = event.end || event.endDatetime || event.endDate || event.end_date;
  return {
    ...event,
    start,
    end,
    title: event.title || event.name || 'Untitled Event',
    location: event.location || event.eventLocation || '',
    status: event.status || '',
    description: event.description || event.eventDescription || '',
    tags: event.tags || [],
  };
};

/* ─────────────────────────────────────────────
   Dedup + Grouping utilities  (strict ownership)
   ───────────────────────────────────────────── */

/** Deduplicate unified items by sourceKey — keeps the "most useful" record on collision */
function dedupeBySourceKey(items) {
  const map = new Map();
  const score = (x) =>
    (x.type === 'assignment' ? 10 : 0) +
    (x.completedAt ? 3 : 0) +
    (x.parentId ? 2 : 0) +
    Math.min((x.title || '').length / 40, 2);

  for (const item of items) {
    const key = item.sourceKey;
    const prev = map.get(key);
    if (!prev) {
      map.set(key, item);
    } else if (score(item) > score(prev)) {
      map.set(key, item);
    }
  }
  return Array.from(map.values());
}

/** Merge assignments with identical title+date into one (preserves ×N badge) */
function mergeTitleDateDupes(assignments) {
  const byKey = new Map();
  assignments.forEach(a => {
    const key = `${a.title}__${a.date}`;
    if (!byKey.has(key)) {
      byKey.set(key, { ...a, duplicateCount: 1 });
    } else {
      byKey.get(key).duplicateCount += 1;
    }
  });
  return Array.from(byKey.values());
}

/** Assign each task a parentId pointing to its nearest upcoming session */
function assignParentIds(events, tasks) {
  const sortedEvents = [...events].sort(
    (a, b) => (parseDate(a.date)?.getTime() ?? 0) - (parseDate(b.date)?.getTime() ?? 0)
  );
  return tasks.map(task => {
    const tTime = parseDate(task.date)?.getTime() ?? 0;
    const parent = sortedEvents.find(e => (parseDate(e.date)?.getTime() ?? 0) >= tTime)
      || sortedEvents[sortedEvents.length - 1]
      || null;
    return { ...task, parentId: parent?.id ?? null };
  });
}

/**
 * Build session groups — single source of truth for Active vs Completed routing.
 * Each session can only appear in ONE list based on isFullyComplete.
 * Sessions with 0 tasks are kept in Active (never auto-completed).
 */
function groupBySession(events, tasksWithParents) {
  const tasksByParent = new Map();
  tasksWithParents.forEach(t => {
    const pid = t.parentId ?? '__unassigned__';
    if (!tasksByParent.has(pid)) tasksByParent.set(pid, []);
    tasksByParent.get(pid).push(t);
  });

  const sortedEvents = [...events].sort(
    (a, b) => (parseDate(a.date)?.getTime() ?? 0) - (parseDate(b.date)?.getTime() ?? 0)
  );

  const groups = sortedEvents.map(event => {
    const children = (tasksByParent.get(event.id) ?? []).sort(
      (a, b) => (parseDate(a.date)?.getTime() ?? 0) - (parseDate(b.date)?.getTime() ?? 0)
    );
    const total = children.length;
    const done = children.filter(t => t.completed).length;
    // 0-task sessions never become "fully complete"
    const isFullyComplete = total > 0 && done === total;

    const completedAt = isFullyComplete
      ? children.map(t => t.completedAt).filter(Boolean).sort().pop() ?? null
      : null;

    return {
      ...event,                // id, title, date, type, endeavorName, sourceKey, raw
      assignments: children,   // stored as "assignments" for EventGroup compat
      totalTasks: total,
      doneTasks: done,
      hasIncompleteTasks: total > 0 && done < total,
      isFullyComplete,
      completedAt,
    };
  });

  // Handle orphan tasks (no parent event)
  const orphans = tasksByParent.get('__unassigned__');
  if (orphans && orphans.length > 0) {
    const sorted = [...orphans].sort(
      (a, b) => (parseDate(a.date)?.getTime() ?? 0) - (parseDate(b.date)?.getTime() ?? 0)
    );
    const total = sorted.length;
    const done = sorted.filter(t => t.completed).length;
    const fullyComplete = total > 0 && done === total;
    groups.push({
      id: '__unassigned__',
      title: 'Upcoming Tasks',
      date: sorted[0]?.date || new Date().toISOString(),
      type: 'event',
      sourceKey: 'event-__unassigned__',
      endeavorName: '',
      raw: null,
      assignments: sorted,
      totalTasks: total,
      doneTasks: done,
      hasIncompleteTasks: total > 0 && done < total,
      isFullyComplete: fullyComplete,
      completedAt: fullyComplete
        ? sorted.map(t => t.completedAt).filter(Boolean).sort().pop() ?? null
        : null,
    });
  }

  return groups;
}

/**
 * urgencyBucket — classify session by time proximity for visual hierarchy.
 * Returns 'overdue' | 'soon' | 'upcoming' | 'later'
 */
function urgencyBucket(dateStr) {
  const d = parseDate(dateStr);
  if (!d) return 'upcoming';
  const days = (d.getTime() - Date.now()) / 86400000;
  if (days < 0) return 'overdue';
  if (days <= 3) return 'soon';
  if (days <= 14) return 'upcoming';
  return 'later';
}

/**
 * selectHero — single source of truth for the hero session.
 * Returns { group, incomplete, earliestDue } or null.
 * Priority: sessions with overdue tasks first, then soonest due.
 */
function selectHero(groups) {
  const candidates = groups
    .filter(g => g.totalTasks > 0 && g.hasIncompleteTasks)
    .map(g => {
      const incomplete = g.assignments.filter(t => !t.completed);
      const sorted = [...incomplete].sort(
        (a, b) => (parseDate(a.date)?.getTime() ?? 0) - (parseDate(b.date)?.getTime() ?? 0)
      );
      return { group: g, incomplete, earliestDue: sorted[0] ?? null };
    })
    .filter(c => c.earliestDue !== null);

  if (candidates.length === 0) return null;

  const now = Date.now();
  candidates.sort((a, b) => {
    const aTime = parseDate(a.earliestDue.date)?.getTime() ?? 0;
    const bTime = parseDate(b.earliestDue.date)?.getTime() ?? 0;
    const aOver = aTime < now ? 0 : 1;
    const bOver = bTime < now ? 0 : 1;
    if (aOver !== bOver) return aOver - bOver;
    return aTime - bTime;
  });

  return candidates[0];
}

/** Contextual suggestion — only show when it adds real info */
const generateSuggestion = (hero, allGroups) => {
  if (!hero) return null;
  const session = hero.group;
  const d = parseDate(session.date);
  const now = new Date();
  if (!d) return null;
  const hoursAway = (d - now) / 3600000;

  // Overdue tasks in this session
  const overdueInSession = hero.incomplete.filter(t => isPast(t.date)).length;
  if (overdueInSession > 0) return `This session has ${overdueInSession} overdue task${overdueInSession > 1 ? 's' : ''} — take action now.`;

  // Time-sensitive
  if (hoursAway > 0 && hoursAway < 3) return `Session starts in less than 3 hours. Make sure you're prepared!`;
  if (hoursAway > 0 && hoursAway < 24) return `Session is today — review your tasks while it's fresh.`;

  // Only show "review materials" if tasks suggest reading/review
  const hasReviewTask = hero.incomplete.some(t =>
    /read|review|material|chapter|slides|notes|study|prepare/i.test(t.title)
  );
  if (hasReviewTask && hoursAway > 0) return 'Consider reviewing materials before this session.';

  // Global overdue count
  const totalOverdue = allGroups.reduce((n, g) =>
    n + g.assignments.filter(a => !a.completed && isPast(a.date)).length, 0);
  if (totalOverdue > 0) return `You have ${totalOverdue} overdue item${totalOverdue > 1 ? 's' : ''} — tackle the oldest first.`;

  return null;
};

/* ─────────────────────────────────────────────
   Component
   ───────────────────────────────────────────── */

function UnifiedActivities({ courseEndeavorId, courseName }) {
  const _navigate = useNavigate(); // eslint-disable-line no-unused-vars
  const { user } = useAuth();
  const {
    getFilteredEndeavors,
    getFilteredEvents,
    getFilteredAssignments,
    isLoading,
    clearCache,
    updateAssignment: dataUpdateAssignment,
    updateCachedAssignment,
    updateCachedEvent,
    deleteAssignment,
    deleteEvent,
    deleteEventSeries,
    markAssignmentCompleted: dataMarkComplete,
    toggleAssignmentCompleted,
  } = useData();
  const _isMobile = useIsMobile(); // eslint-disable-line no-unused-vars
  const userId = user?.id || user?.userId;

  /* ── Local state ── */
  const [events, setEvents] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [, setEndeavors] = useState([]);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [showCompleted, setShowCompleted] = useState(false);
  const [expandedEventIds, setExpandedEventIds] = useState([]);
  const [completingId, setCompletingId] = useState(null);
  // Detail modals (match main timeline pattern)
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [highlightedSessionId, setHighlightedSessionId] = useState(null);
  // Confirm-complete modal: list of tasks to review before bulk completing
  const [confirmTasks, setConfirmTasks] = useState(null);
  const [confirmChecked, setConfirmChecked] = useState(new Set());
  // Create event/assignment modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createModalStep, setCreateModalStep] = useState('choose');
  // Track whether auto-expand has run for a given data set
  const autoExpandedRef = useRef(false);

  /* Stable refs to DataContext methods */
  const getFilteredEndeavorsRef = useRef(getFilteredEndeavors);
  const getFilteredEventsRef = useRef(getFilteredEvents);
  const getFilteredAssignmentsRef = useRef(getFilteredAssignments);
  useEffect(() => {
    getFilteredEndeavorsRef.current = getFilteredEndeavors;
    getFilteredEventsRef.current = getFilteredEvents;
    getFilteredAssignmentsRef.current = getFilteredAssignments;
  }, [getFilteredEndeavors, getFilteredEvents, getFilteredAssignments]);

  /* ── Data fetching ── */

  const loadEndeavors = useCallback(async () => {
    if (!userId) return [];
    if (courseEndeavorId) {
      const single = [{ id: Number(courseEndeavorId), endeavorId: Number(courseEndeavorId), name: courseName || 'Course' }];
      setEndeavors(single);
      return single;
    }
    try {
      setError(null);
      const list = await getFilteredEndeavorsRef.current(userId);
      setEndeavors(list);
      return list;
    } catch (err) {
      setError(err);
      return [];
    }
  }, [userId, courseEndeavorId, courseName]);

  const loadData = useCallback(async (endeavorsList) => {
    try {
      setError(null);
      const nameById = new Map(endeavorsList.map(e => [Number(e.endeavorId || e.id), e.name]));

      // Load events
      const eventParams = {};
      if (courseEndeavorId) eventParams.endeavorId = courseEndeavorId;
      const eventList = await getFilteredEventsRef.current(eventParams);
      const enrichedEvents = eventList.map(ev => {
        const eid = ev.endeavorId || ev.endeavorID || ev.endeavor_id;
        return { ...ev, endeavorId: eid, endeavorName: nameById.get(Number(eid)) || ev.endeavorName || courseName || 'Course' };
      });
      setEvents(enrichedEvents);

      // Load assignments
      const assignParams = {};
      if (courseEndeavorId) assignParams.endeavorId = courseEndeavorId;
      const assignList = await getFilteredAssignmentsRef.current(assignParams);
      const enrichedAssignments = assignList.map(a => {
        const eid = a.endeavorId || a.endeavorID || a.endeavor_id;
        return { ...a, endeavorId: eid, endeavorName: nameById.get(Number(eid)) || a.endeavorName || courseName || 'Course' };
      });
      setAssignments(enrichedAssignments);
      autoExpandedRef.current = false; // allow auto-expand to re-run with new data
    } catch (err) {
      setError(err);
    }
  }, [courseEndeavorId, courseName]);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    loadEndeavors().then(list => { if (!cancelled) loadData(list); });
    return () => { cancelled = true; };
  }, [userId, loadEndeavors, loadData]);

  const handleRefresh = useCallback(async () => {
    const list = await loadEndeavors();
    await loadData(list);
  }, [loadEndeavors, loadData]);

  /* ── Completion toggling (instant — delegates to DataContext) ── */

  const handleToggleCompletion = useCallback(async (item) => {
    if (item.type !== 'assignment') return;
    const assignment = item.raw;
    if (!assignment) return;
    const id = assignment.assignmentId || assignment.id;
    const makeCompleted = !item.completed;

    // Optimistic local UI update (instant green fill / unfill)
    setAssignments(prev => prev.map(a => {
      const aid = a.assignmentId || a.id;
      if (aid !== id) return a;
      return {
        ...a,
        completed: makeCompleted,
        status: makeCompleted ? 'Completed' : 'Pending',
        submissionStatus: makeCompleted ? 'Completed' : 'Pending',
        completedDatetime: makeCompleted ? new Date().toISOString() : null,
      };
    }));

    try {
      setCompletingId(id);
      // Delegate to DataContext — handles API call, cache update, usage tracking
      await toggleAssignmentCompleted(assignment, { source: 'activities-tab' });
      window.dispatchEvent(new Event('assignments:updated'));
    } catch (err) {
      // Revert on failure
      setAssignments(prev => prev.map(a => {
        const aid = a.assignmentId || a.id;
        if (aid !== id) return a;
        return {
          ...a,
          completed: !makeCompleted,
          status: !makeCompleted ? 'Completed' : 'Pending',
          submissionStatus: !makeCompleted ? 'Completed' : 'Pending',
        };
      }));
    } finally {
      setCompletingId(null);
    }
  }, [toggleAssignmentCompleted]);

  /* ── Normalize (add sourceKey + completedAt for strict ownership) ── */

  const normalizedEvents = useMemo(() =>
    events.map(e => {
      const id = e.eventId || e.id;
      return {
        id,
        title: e.name || e.title || 'Untitled Event',
        date: e.startDatetime || e.eventDate,
        type: 'event',
        completed: (() => { const d = parseDate(e.startDatetime || e.eventDate); return d ? d < new Date() : false; })(),
        endeavorName: e.endeavorName,
        sourceKey: `event-${id}`,
        raw: e,
      };
    })
  , [events]);

  const normalizedAssignments = useMemo(() =>
    assignments.map(a => {
      const id = a.assignmentId || a.id;
      return {
        id,
        title: a.name || a.title || 'Untitled Assignment',
        date: a.dueDatetime || a.dueDate,
        type: 'assignment',
        completed: Boolean(a.completed || a.status === 'Completed' || a.submissionStatus === 'Completed'),
        completedAt: a.completedDatetime || a.completedDate || null,
        endeavorName: a.endeavorName,
        sourceKey: `assignment-${id}`,
        raw: a,
      };
    })
  , [assignments]);

  /* ── Dedup: sourceKey pass → title+date merge for assignments ── */
  const deduped = useMemo(() => {
    const all = [...normalizedEvents, ...normalizedAssignments];
    const bySource = dedupeBySourceKey(all);
    const evts = bySource.filter(i => i.type === 'event');
    const tasks = mergeTitleDateDupes(bySource.filter(i => i.type === 'assignment'));
    return { events: evts, tasks };
  }, [normalizedEvents, normalizedAssignments]);

  /* ── Assign parentIds + build session groups (single source of truth) ── */
  const sessionGroups = useMemo(() => {
    const tasksWithParents = assignParentIds(deduped.events, deduped.tasks);
    return groupBySession(deduped.events, tasksWithParents);
  }, [deduped]);

  /* ── Strict ownership: Active vs Completed ── */
  const activeGroups = useMemo(
    () => sessionGroups.filter(g => !g.isFullyComplete),
    [sessionGroups]
  );

  const completedGroups = useMemo(
    () => [...sessionGroups.filter(g => g.isFullyComplete)].sort(
      (a, b) => (b.completedAt ?? '').localeCompare(a.completedAt ?? '')
    ),
    [sessionGroups]
  );

  /* ── DEV: strict ownership assertion ── */
  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const activeIds = new Set(activeGroups.map(g => g.id));
    const overlap = completedGroups.some(g => activeIds.has(g.id));
    if (overlap) console.warn('\u26A0\uFE0F Strict ownership violated: session appears in both Active and Completed lists');
  }

  /* ── Filter groups ── */
  const applyGroupFilter = useCallback((groups, filter) => {
    switch (filter) {
      case 'nextClass':
        return computeNextClassGroups(groups);
      case 'next14':
        return groups
          .map(g => ({
            ...g,
            assignments: g.assignments.filter(a => isWithinDays(a.date, 14)),
          }))
          .filter(g => isWithinDays(g.date, 14) || g.assignments.length > 0);
      case 'overdue':
        return groups
          .map(g => ({
            ...g,
            assignments: g.assignments.filter(a => isPast(a.date) && !a.completed),
          }))
          .filter(g => g.assignments.length > 0);
      default:
        return groups;
    }
  }, []);

  const filteredGroups = useMemo(
    () => applyGroupFilter(activeGroups, activeFilter),
    [activeGroups, activeFilter, applyGroupFilter]
  );

  /* ── Hero: single source of truth via selectHero ── */
  const hero = useMemo(() => selectHero(activeGroups), [activeGroups]);
  const heroSessionId = hero?.group?.id ?? null;

  /* ── Auto-expand: always keep hero session expanded ── */
  useEffect(() => {
    if (!heroSessionId) return;
    setExpandedEventIds(prev =>
      prev.includes(heroSessionId) ? prev : [...prev, heroSessionId]
    );
  }, [heroSessionId]);

  /* ── Initial auto-expand: hero + overdue groups ── */
  useEffect(() => {
    if (autoExpandedRef.current || filteredGroups.length === 0) return;
    const overdueGroupIds = filteredGroups
      .filter(g => g.assignments.some(a => !a.completed && isPast(a.date)))
      .map(g => g.id);
    const auto = [heroSessionId, ...overdueGroupIds].filter(Boolean);
    setExpandedEventIds(prev => [...new Set([...prev, ...auto])]);
    autoExpandedRef.current = true;
  }, [filteredGroups, heroSessionId]);

  /* ── DEV: hero session must exist in active groups ── */
  if (process.env.NODE_ENV === 'development' && heroSessionId) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const activeIdSet = new Set(activeGroups.map(g => g.id));
    if (!activeIdSet.has(heroSessionId)) console.warn('\u26A0\uFE0F Hero session missing from active list');
  }

  const suggestion = useMemo(() => generateSuggestion(hero, activeGroups), [hero, activeGroups]);

  /* ── Progress (count tasks, not sessions) ── */
  const allTasks = useMemo(() => sessionGroups.flatMap(g => g.assignments), [sessionGroups]);
  const totalItems = allTasks.length;
  const completedCount = allTasks.filter(t => t.completed).length;
  const progressPercent = totalItems > 0 ? Math.round((completedCount / totalItems) * 100) : 0;

  /* ── Toggle expand ── */
  const toggleExpand = useCallback((id) => {
    setExpandedEventIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  }, []);

  /* ── Focus hero session: scroll + highlight ── */
  const focusHeroSession = useCallback((sessionId) => {
    if (!sessionId) return;
    setExpandedEventIds(prev =>
      prev.includes(sessionId) ? prev : [...prev, sessionId]
    );
    setTimeout(() => {
      const el = document.getElementById(`event-group-${sessionId}`);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setHighlightedSessionId(sessionId);
      setTimeout(() => setHighlightedSessionId(null), 1500);
    }, 100);
  }, []);

  /* ── Hero CTA: complete single task instantly ── */
  const handleHeroComplete = useCallback(async () => {
    if (!hero) return;
    const incomplete = hero.incomplete.filter(a => a.type === 'assignment');
    if (incomplete.length !== 1) return; // single-task only; multi uses review modal
    const task = incomplete[0];
    handleToggleCompletion(task);
  }, [hero, handleToggleCompletion]);

  /* ── Review Tasks modal: open for multi-task hero CTA ── */
  const handleOpenReviewTasks = useCallback(() => {
    if (!hero) return;
    const incomplete = hero.incomplete.filter(a => a.type === 'assignment');
    if (incomplete.length === 0) return;
    setConfirmTasks(incomplete);
    setConfirmChecked(new Set(incomplete.map(t => t.id))); // all checked by default
  }, [hero]);

  const handleConfirmComplete = useCallback(async () => {
    if (!confirmTasks || confirmChecked.size === 0) return;
    const toComplete = confirmTasks.filter(t => confirmChecked.has(t.id));
    setConfirmTasks(null);

    setCompletingId('__hero_completing__');
    const ids = new Set(toComplete.map(a => a.id));

    // Optimistic update
    setAssignments(prev => prev.map(a => {
      const aid = a.assignmentId || a.id;
      if (!ids.has(aid)) return a;
      return {
        ...a,
        completed: true,
        status: 'Completed',
        submissionStatus: 'Completed',
        completedDatetime: new Date().toISOString(),
      };
    }));

    try {
      for (const task of toComplete) {
        const raw = task.raw;
        if (raw) {
          await toggleAssignmentCompleted(raw, { source: 'activities-tab' });
        }
      }
      window.dispatchEvent(new Event('assignments:updated'));
    } catch (err) {
      setAssignments(prev => prev.map(a => {
        const aid = a.assignmentId || a.id;
        if (!ids.has(aid)) return a;
        return {
          ...a,
          completed: false,
          status: 'Pending',
          submissionStatus: 'Pending',
          completedDatetime: null,
        };
      }));
    } finally {
      setCompletingId(null);
    }
  }, [confirmTasks, confirmChecked, toggleAssignmentCompleted]);

  /* ── Navigation helpers ── */
  const handleItemClick = useCallback((item) => {
    if (!item) return;
    if (item.type === 'event') {
      setSelectedEvent(normalizeEventForModal(item.raw || item));
    } else {
      setSelectedAssignment(item.raw || item);
    }
  }, []);

  const handleEventRowClick = useCallback((eventItem) => {
    if (!eventItem) return;
    setSelectedEvent(normalizeEventForModal(eventItem.raw || eventItem));
  }, []);

  /* ── Modal mutation handlers ── */
  // onUpdate: UI-only — updates modal display immediately (called by useDetailsModalSave for optimistic updates)
  const handleAssignmentUpdate = useCallback((updatedData) => {
    setSelectedAssignment(updatedData);
  }, []);

  // onSaved: called AFTER API success — surgically update cache + refresh activities list
  const handleAssignmentSaved = useCallback(async (serverData, id) => {
    try {
      if (updateCachedAssignment) {
        updateCachedAssignment(id, serverData);
      }
      handleRefresh();
      window.dispatchEvent(new Event('assignments:updated'));
    } catch (err) {
      console.warn('Post-save refresh failed (non-fatal):', err);
    }
  }, [updateCachedAssignment, handleRefresh]);

  const handleAssignmentMarkComplete = useCallback(async (assignmentData) => {
    try {
      const id = assignmentData.assignmentId || assignmentData.id;
      const willBeCompleted = assignmentData.completed;
      let response;
      if (willBeCompleted && dataMarkComplete) {
        response = await dataMarkComplete(id);
      } else {
        response = await dataUpdateAssignment(id, assignmentData);
      }
      if (!response?.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');
        throw new Error(`Failed to update completion status: ${errorText}`);
      }
      clearCache('assignments');
      clearCache(`assignment_${id}`);
      setSelectedAssignment(null);
      handleRefresh();
      window.dispatchEvent(new Event('assignments:updated'));
    } catch (err) {
      console.error('Assignment completion toggle failed:', err);
      throw err;
    }
  }, [dataMarkComplete, dataUpdateAssignment, clearCache, handleRefresh]);

  // onUpdate: UI-only — updates modal display immediately
  const handleEventUpdate = useCallback((updatedData) => {
    setSelectedEvent(updatedData);
  }, []);

  // onSaved: called AFTER API success — surgically update cache + refresh activities list
  const handleEventSaved = useCallback(async (serverData, id) => {
    try {
      if (updateCachedEvent) {
        updateCachedEvent(id, serverData);
      }
      handleRefresh();
      window.dispatchEvent(new CustomEvent('scoutiq:eventUpdated', {
        detail: { eventId: id, updatedEvent: serverData }
      }));
    } catch (err) {
      console.warn('Post-save refresh failed (non-fatal):', err);
    }
  }, [updateCachedEvent, handleRefresh]);

  const handleCreateEvent = useCallback(() => {
    setCreateModalStep('event');
    setShowCreateModal(true);
  }, []);

  const handleCreateAssignment = useCallback(() => {
    setCreateModalStep('assignment');
    setShowCreateModal(true);
  }, []);

  const handleModalCreated = useCallback(({ type, item }) => {
    setShowCreateModal(false);
    handleRefresh();
  }, [handleRefresh]);

  /* ── Loading state ── */
  const loading = isLoading(`endeavors_user-${userId}`);

  /* ── Render ── */

  if (loading && events.length === 0 && assignments.length === 0) {
    return (
      <div className="activities-layout">
        <div className="activities-loading">
          <div className="loading-spinner" />
          <p>Loading activities...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="activities-layout">
        <div className="activities-error">
          <span className="error-icon">⚠️</span>
          <p>Failed to load activities</p>
          <button className="btn btn-secondary" onClick={handleRefresh}>🔄 Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="activities-layout">
      {/* ── Filters ── */}
      <div className="activities-filter-bar">
        <div className="segmented-control" role="radiogroup" aria-label="Timeline filter">
          {FILTER_OPTIONS.map(opt => (
            <button
              key={opt.value}
              className={`segment-btn ${activeFilter === opt.value ? 'active' : ''}`}
              onClick={() => setActiveFilter(opt.value)}
              role="radio"
              aria-checked={activeFilter === opt.value}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <div className="activities-actions">
          <button className="activities-add-btn" onClick={handleCreateEvent} aria-label="Create event" title="New Event">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Event
          </button>
          <button className="activities-add-btn" onClick={handleCreateAssignment} aria-label="Create assignment" title="New Assignment">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Assignment
          </button>
        </div>
      </div>

      {/* ── Hero Card (session-based) ── */}
      {hero ? (
        <div className="activitiesTopStack">
          <NextActionCard
            hero={hero}
            isHeroExpanded={expandedEventIds.includes(heroSessionId)}
            onFocusSession={() => focusHeroSession(heroSessionId)}
            onComplete={handleHeroComplete}
            onReviewTasks={handleOpenReviewTasks}
            completingId={completingId}
          />
          {suggestion && <ScoutSuggestion text={suggestion} />}
          <ProgressFooter
            completedCount={completedCount}
            totalCount={totalItems}
            percent={progressPercent}
          />
        </div>
      ) : (
        /* Edge case: zero tasks due — clear state card */
        activeGroups.length === 0 && completedGroups.length > 0 && (
          <div className="nac-clear-state">
            <div className="nac-clear-icon">✅</div>
            <h3 className="nac-clear-title">You're all clear</h3>
            <p className="nac-clear-sub">No upcoming tasks. Enjoy the break!</p>
          </div>
        )
      )}

      {/* ── Grouped Timeline ── */}
      {filteredGroups.length > 0 ? (
        <div className="grouped-timeline">
          {filteredGroups.map(group => (
            <EventGroup
              key={group.id}
              event={group}
              expanded={expandedEventIds.includes(group.id)}
              toggle={toggleExpand}
              formatDate={formatRelativeDate}
              onItemClick={handleItemClick}
              onEventClick={handleEventRowClick}
              onToggle={handleToggleCompletion}
              completingId={completingId}
              highlighted={highlightedSessionId === group.id}
              urgency={urgencyBucket(group.date)}
            />
          ))}
        </div>
      ) : (
        <div className="activities-empty">
          <div className="activities-empty-icon">🎉</div>
          <h3>All caught up!</h3>
          <p>{activeFilter !== 'all' ? 'No items match this filter.' : 'No upcoming activities.'}</p>
        </div>
      )}

      {/* ── Completed (sessions with all tasks done) ── */}
      {completedGroups.length > 0 && (
        <CompletedSection
          completedGroups={completedGroups}
          formatDate={formatRelativeDate}
          onItemClick={handleItemClick}
          onEventClick={handleEventRowClick}
          onToggle={handleToggleCompletion}
          completingId={completingId}
          showCompleted={showCompleted}
          setShowCompleted={setShowCompleted}
        />
      )}

      {/* Progress strip now rendered under NextActionCard */}

      {/* ── One-time onboarding coachmarks ── */}
      {filteredGroups.length > 0 && (
        <>
          <Coachmark
            hintKey="row"
            selector=".event-row"
            text="Click an event to view details"
            placement="bottom"
            delay={1200}
          />
          <Coachmark
            hintKey="circle"
            selector=".eg-assignment-toggle.is-incomplete"
            text="Click the circle to mark complete"
            placement="right"
            delay={1800}
          />
        </>
      )}

      {/* ── Confirm Complete Modal ── */}
      <CenteredModal
        isOpen={!!confirmTasks}
        onClose={() => setConfirmTasks(null)}
        title={`Review Tasks (${confirmTasks?.length ?? 0})`}
      >
        {confirmTasks && (
          <div className="confirm-tasks-modal">
            {/* Context subtitle */}
            {hero?.group && (
              <div className="confirm-tasks-context">
                <span className="confirm-tasks-session">{hero.group.title}</span>
                <span className="confirm-tasks-sep"> · </span>
                <span className="confirm-tasks-session-date">{formatRelativeDate(hero.group.date)}</span>
              </div>
            )}
            <div className="confirm-tasks-divider" />

            {/* Task rows */}
            <div className="confirm-tasks-list">
              {confirmTasks.map(task => {
                const isSelected = confirmChecked.has(task.id);
                const isOverdue = (() => { const d = parseDate(task.date); return d && d < new Date(); })();
                return (
                  <div
                    key={task.id}
                    className={`confirm-task-row ${isSelected ? 'confirm-task-row--checked' : ''} ${task.completed ? 'confirm-task-row--completed' : ''} ${isOverdue ? 'confirm-task-row--overdue' : ''}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleItemClick(task)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleItemClick(task); }}
                  >
                    <button
                      className={`confirm-task-check ${task.completed ? 'confirm-task-check--completed' : ''} ${isSelected && !task.completed ? 'confirm-task-check--on' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setConfirmChecked(prev => {
                          const next = new Set(prev);
                          if (next.has(task.id)) next.delete(task.id); else next.add(task.id);
                          return next;
                        });
                      }}
                      aria-label={isSelected ? 'Deselect task' : 'Select task'}
                    >
                      {(isSelected || task.completed) && (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      )}
                    </button>
                    <div className="confirm-task-body">
                      <span className="confirm-task-title">{task.title}</span>
                    </div>
                    <span className="confirm-task-date">
                      {parseDate(task.date)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) ?? ''}
                    </span>
                    <span className="confirm-task-chevron">›</span>
                  </div>
                );
              })}
            </div>

            {/* Footer */}
            <div className="confirm-tasks-footer">
              <span className="confirm-tasks-count">
                {confirmChecked.size} of {confirmTasks.length} selected
              </span>
              <div className="confirm-tasks-actions">
                <button
                  className="next-action-btn next-action-btn--secondary"
                  onClick={() => setConfirmTasks(null)}
                >
                  Cancel
                </button>
                <button
                  className="next-action-btn next-action-btn--primary"
                  onClick={handleConfirmComplete}
                  disabled={confirmChecked.size === 0}
                >
                  Complete Selected
                </button>
              </div>
            </div>
          </div>
        )}
      </CenteredModal>

      {/* ── Assignment Detail Modal ── */}
      <CenteredModal
        isOpen={!!selectedAssignment}
        onClose={() => setSelectedAssignment(null)}
        title={selectedAssignment?.title || selectedAssignment?.name}
        onDelete={selectedAssignment ? async () => {
          await deleteAssignment(selectedAssignment.assignmentId || selectedAssignment.id);
          setSelectedAssignment(null);
          handleRefresh();
        } : undefined}
      >
        {selectedAssignment && (
          <AssignmentDetails
            assignment={selectedAssignment}
            onClose={() => setSelectedAssignment(null)}
            onUpdate={handleAssignmentUpdate}
            onSaved={handleAssignmentSaved}
            onMarkComplete={handleAssignmentMarkComplete}
            onEdit={() => { console.log('Edit assignment:', selectedAssignment.id); }}
          />
        )}
      </CenteredModal>

      {/* ── Event Detail Modal ── */}
      <CenteredModal
        isOpen={!!selectedEvent}
        onClose={() => setSelectedEvent(null)}
        title={selectedEvent?.title || selectedEvent?.name}
        isRecurringEvent={selectedEvent && (selectedEvent.isRecurring || selectedEvent.recurrenceRule || selectedEvent.parentEventId)}
        onDelete={selectedEvent ? async () => {
          await deleteEvent(selectedEvent.eventId || selectedEvent.id);
          clearCache('user-events');
          clearCache('user-assignments');
          setSelectedEvent(null);
          handleRefresh();
        } : undefined}
        onDeleteSeries={selectedEvent ? async () => {
          const parentId = selectedEvent.parentEventId || selectedEvent.eventId || selectedEvent.id;
          await deleteEventSeries(parentId);
          clearCache('user-events');
          clearCache('user-assignments');
          setSelectedEvent(null);
          handleRefresh();
        } : undefined}
      >
        {selectedEvent && (
          <EventDetails
            event={selectedEvent}
            onClose={() => setSelectedEvent(null)}
            onUpdate={handleEventUpdate}
            onSaved={handleEventSaved}
            onEdit={() => { console.log('Edit event:', selectedEvent.id); }}
            onDelete={async (eventId) => {
              await deleteEvent(eventId);
              clearCache('user-events');
              clearCache('user-assignments');
              setSelectedEvent(null);
              handleRefresh();
            }}
          />
        )}
      </CenteredModal>

      {/* ── Create Event / Assignment Modal ── */}
      <AddToCalendarModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={handleModalCreated}
        initialStep={createModalStep}
        prefill={{
          selectedDate: new Date().toISOString().split('T')[0],
          endeavorId: courseEndeavorId || null,
        }}
      />
    </div>
  );
}

export default UnifiedActivities;
